import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, BarChart3 } from "lucide-react";
import { api } from "../../lib/api";
import { toast } from "sonner";

const TYPE_LABELS = {
    "ortografía": "Ortografía",
    "gramática": "Gramática",
    "puntuación": "Puntuación",
    "concordancia": "Concordancia",
    "redacción": "Redacción",
    "otro": "Otro",
};

const TYPE_COLORS = [
    "bg-violet-500", "bg-blue-500", "bg-amber-500", "bg-rose-500", "bg-emerald-500", "bg-slate-500",
];

export default function ReporteErroresCurso() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [course, setCourse] = useState(null);
    const [report, setReport] = useState(null);
    const [loading, setLoading] = useState(true);

    async function load() {
        setLoading(true);
        try {
            const [cr, rr] = await Promise.all([
                api.get("/courses"),
                api.get(`/courses/${id}/grammar-report`),
            ]);
            const found = (cr.data || []).find((c) => c.id === id);
            setCourse(found || null);
            setReport(rr.data);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Error cargando el reporte");
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => { load(); }, [id]);

    if (loading) return <div className="p-10 text-edu-muted">Cargando…</div>;
    if (!report) return <div className="p-10 text-edu-muted">No se pudo cargar el reporte.</div>;

    return (
        <div className="p-6 md:p-10 fade-in min-h-screen max-w-3xl mx-auto">
            <button onClick={() => navigate(-1)} className="inline-flex items-center gap-2 text-sm text-edu-body hover:text-edu-navy mb-6">
                <ArrowLeft size={16} /> Volver
            </button>

            <div className="mb-6">
                <h1 className="text-2xl font-semibold inline-flex items-center gap-2"><BarChart3 size={22} className="text-edu-navy" /> Reporte de errores de gramática</h1>
                <p className="text-edu-muted mt-1">{course?.name || "Curso"} {course?.sections?.length ? `· ${course.sections.join(", ")}` : ""}</p>
            </div>

            <div className="grid grid-cols-3 gap-3 mb-6">
                <div className="edu-card p-4 text-center">
                    <p className="text-2xl font-semibold">{report.total_submissions_checked}</p>
                    <p className="text-xs text-edu-muted mt-1">Entregas corregidas</p>
                </div>
                <div className="edu-card p-4 text-center">
                    <p className="text-2xl font-semibold">{report.total_with_errors}</p>
                    <p className="text-xs text-edu-muted mt-1">Con algún error</p>
                </div>
                <div className="edu-card p-4 text-center">
                    <p className="text-2xl font-semibold">{report.total_errors}</p>
                    <p className="text-xs text-edu-muted mt-1">Errores totales</p>
                </div>
            </div>

            {report.by_type.length === 0 ? (
                <div className="edu-card p-10 text-center text-edu-muted">
                    Todavía no hay suficientes entregas corregidas en este curso para armar el reporte.
                </div>
            ) : (
                <>
                    <div className="edu-card p-6 mb-6">
                        <h2 className="text-sm font-semibold text-edu-navy mb-4">Dónde se equivocan más los alumnos</h2>
                        <div className="space-y-3">
                            {report.by_type.map((t, i) => (
                                <div key={t.type}>
                                    <div className="flex items-center justify-between text-sm mb-1">
                                        <span className="font-medium">{TYPE_LABELS[t.type] || t.type}</span>
                                        <span className="text-edu-muted">{t.percent}% ({t.count})</span>
                                    </div>
                                    <div className="w-full h-2.5 rounded-full bg-edu-soft overflow-hidden">
                                        <div className={`h-full ${TYPE_COLORS[i % TYPE_COLORS.length]}`} style={{ width: `${t.percent}%` }} />
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {report.by_assignment.length > 0 && (
                        <div className="edu-card p-6">
                            <h2 className="text-sm font-semibold text-edu-navy mb-4">Tareas con más errores</h2>
                            <div className="space-y-2">
                                {report.by_assignment.map((a) => (
                                    <div key={a.assignment_id} className="flex items-center justify-between text-sm p-2.5 rounded-lg bg-edu-soft">
                                        <span>{a.title}</span>
                                        <span className="text-edu-muted">{a.error_count} error{a.error_count !== 1 ? "es" : ""}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}
